import React from 'react';
import { Translation } from 'react-i18next';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

// The app-level safety net (P26).
//
// WHY THIS EXISTS. Frontend-web had no error boundary anywhere, so ONE render
// that threw — a refused booking whose error OBJECT was put straight into JSX,
// a `.map` on something that was not an array — unmounted the entire React
// tree. The user was left on a blank white page with no message, no menu and no
// way back except the address bar, and whatever the server had said was lost.
//
// A boundary cannot catch errors in event handlers, timers or promises (those
// never tear the tree down anyway); it catches exactly the render/lifecycle
// throws that produce the white page.
//
// `resetKey` — when it changes the boundary clears itself. App.jsx passes the
// current route, so navigating away from the screen that crashed shows the new
// screen instead of the same error card forever.
//
// Translation uses the render-prop form because this is a class component and
// must keep working even when the crash happened far from any hook. Every
// string carries its English fallback so the card renders before the locale
// keys are merged — and, as the last line of defence, even if i18n itself is
// what failed.
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
        this.handleRetry = this.handleRetry.bind(this);
        this.handleHome = this.handleHome.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Kept in the console for whoever is debugging; never shown to the user.
        console.error('ErrorBoundary caught a render error:', error, info?.componentStack);
        if (typeof this.props.onError === 'function') {
            try { this.props.onError(error, info); } catch { /* a reporter must not re-throw */ }
        }
    }

    componentDidUpdate(prevProps) {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null });
        }
    }

    handleRetry() {
        this.setState({ error: null });
    }

    handleHome() {
        this.setState({ error: null });
        // HashRouter app: the dashboard is "#/". Assigning the hash works from
        // outside the router too, which is where the outermost boundary sits.
        try { window.location.hash = '#/'; } catch { /* non-browser */ }
    }

    renderFallback(t) {
        const tr = (key, fallback) => {
            try {
                const out = typeof t === 'function' ? t(key, fallback) : fallback;
                return typeof out === 'string' && out ? out : fallback;
            } catch {
                return fallback;
            }
        };

        return (
            <div
                role="alert"
                data-testid="app-error-boundary"
                className="w-full min-h-[50vh] flex items-center justify-center py-16 px-4"
            >
                <div className="bg-white rounded-2xl shadow-md max-w-md w-full p-8 text-center">
                    <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center">
                        <AlertTriangle className="w-7 h-7" />
                    </div>
                    <h2 data-testid="app-error-boundary-title" className="text-xl font-bold text-[#1a365d] mb-2">
                        {tr('errorBoundary.title', 'This screen ran into a problem')}
                    </h2>
                    <p data-testid="app-error-boundary-message" className="text-sm text-slate-500 mb-6">
                        {tr('errorBoundary.message', 'Nothing you saved has been lost. Try again, or go back to your dashboard.')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            data-testid="app-error-boundary-retry"
                            onClick={this.handleRetry}
                            className="flex-1 px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                        >
                            <RotateCcw className="w-4 h-4" />
                            {tr('errorBoundary.retry', 'Try again')}
                        </button>
                        <button
                            type="button"
                            data-testid="app-error-boundary-home"
                            onClick={this.handleHome}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm text-[#2D8A8A] border border-[#C7D6D5] bg-white hover:bg-[#4ABDBD]/5 flex items-center justify-center gap-2 cursor-pointer"
                        >
                            <Home className="w-4 h-4" />
                            {tr('errorBoundary.home', 'Back to dashboard')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.error) return this.props.children;
        return <Translation>{(t) => this.renderFallback(t)}</Translation>;
    }
}
